import { HOMEWORK_STATE } from '@/lib/utils';
import { ROUTE } from '@/routes/constants';
import { getUser, login as loginAPI, logout as logoutAPI } from '@/services/generatedAPIs';
import { LoginRequest, UserInfoResponse } from '@/services/generatedAPIs.schemas';
import { AuthContextType } from '@/types/types';
import { AxiosRequestConfig } from 'axios';
import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { init } from '@plausible-analytics/tracker';

// 创建 AuthContext
const AuthContext = createContext<AuthContextType | undefined>(undefined);
let schoolName: string = "";

init({
  domain: 'ease.eduhk.hk',
  autoCapturePageviews: true,
  customProperties: (eventName) => { return { schoolName: schoolName }}
})

// 创建一个 AuthProvider 组件
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserInfoResponse | undefined>(sessionStorage.getItem('user') ? JSON.parse(sessionStorage.getItem('user')!) : undefined);
  if (user?.schoolName) {
    schoolName = user?.schoolName;
  }

  const setCurrentUser = (user: UserInfoResponse | undefined) => {
    setUser(user);
  };

  const getCurrentUser = (): UserInfoResponse | undefined => {
    return user;
  }

  const tokenLogin = useCallback(async (): Promise<UserInfoResponse | undefined> => {
    const token = localStorage.getItem('token');
    console.log('[tokenLogin] 开始执行，token 是否存在:', !!token);
    if (!token) return undefined;
    try {
      const res = await getUser(token);
      console.log('[tokenLogin] getUser 返回:', res?.code, res?.message);
      if (res && res.code === 200) {
        setCurrentUser(res.data);
        sessionStorage.setItem('user', JSON.stringify(res.data!));
        schoolName = res.data?.schoolName ?? "";
        console.log('[tokenLogin] user 设置成功:', res.data?.email);
        return res.data!;
      }
    } catch(error) {
      console.error('[tokenLogin] failed:', error);
    }
    return undefined;
  }, []);

  const login = useCallback(async (
    request: LoginRequest,
    options?: AxiosRequestConfig
  ): Promise<{ success: boolean; user?: UserInfoResponse; code?: number; message?: string }> => {
    const response = await loginAPI(request, options);
    if (response.code === 200) {
      const loggedInUser = response.data!.user;
      setUser(loggedInUser);
      localStorage.setItem('token', response.data!.token);
      sessionStorage.setItem('user', JSON.stringify(loggedInUser));
      schoolName = loggedInUser.schoolName ?? "";
      console.log('[AuthContext.login] 登录成功，准备发送 login 广播');
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('auth');
        channel.postMessage({ type: 'login', token: response.data!.token });
        channel.close();
        console.log('[AuthContext.login] login 广播已发送');
      }
      return { success: true, user: loggedInUser };
    }
    return { success: false, code: response.code, message: response.message };
  }, []);

  const logout = useCallback((): void => {
    const token = localStorage.getItem('token');

    // 1. 先调用后端 logout（此时 localStorage.token 还在，能带上 Authorization）
    if (token) {
      logoutAPI().catch((err) => {
        console.warn('[logout] 后端登出接口调用失败（不影响前端登出）:', err);
      });
    }

    // 2. 清理本地状态
    setUser(undefined);
    HOMEWORK_STATE.itemId = undefined;
    HOMEWORK_STATE.tabId = undefined;
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    schoolName = "";

    // 3. 广播登出事件（状态已清理，确保其他标签页收到时一致）
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('auth');
      channel.postMessage({ type: 'logout' });
      channel.close();
    }

    // 4. 最后跳转（给 logoutAPI 请求一点时间发出去）
    window.location.href = ROUTE.LOGIN;
  }, []);

  // 多标签页同步：监听 localStorage token 变化和 BroadcastChannel
  useEffect(() => {
    const handleLogout = () => {
      setUser(undefined);
      sessionStorage.removeItem('user');
      HOMEWORK_STATE.itemId = undefined;
      HOMEWORK_STATE.tabId = undefined;
      schoolName = "";
      window.location.href = ROUTE.LOGIN;
    };

    const handleTokenUpdate = () => {
      tokenLogin().then((user) => {
        if (!user) {
          setUser(undefined);
          sessionStorage.removeItem('user');
        }
      });
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'token' && !event.newValue) {
        handleLogout();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    let channel: BroadcastChannel | undefined;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel('auth');
      channel.onmessage = (event) => {
        console.log('[BroadcastChannel] 收到消息:', event.data);
        if (event.data?.type === 'logout') {
          handleLogout();
        } else if (event.data?.type === 'login') {
          console.log('[BroadcastChannel] 收到 login，token:', !!event.data.token);
          if (event.data.token) {
            localStorage.setItem('token', event.data.token);
          }
          handleTokenUpdate();
        }
      };
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      channel?.close();
    };
  }, [tokenLogin]);

  const value: AuthContextType = {
    user,
    getCurrentUser,
    setCurrentUser,
    login,
    tokenLogin,
    logout,
    isLoggedIn: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 自定义 Hook 来使用 AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
